/**
 * Client-Side Emergency Vault & Local Backup Utility
 * Automatically maintains persistent emergency backups of site config and products
 * directly in browser localStorage / IndexedDB.
 */

const VAULT_PREFIX = 'omnivaso_emergency_vault_';
const LIVE_DRAFT_PREFIX = 'omnivaso_live_draft_';
const MAX_VAULT_ITEMS = 10;
const MIN_SYSTEM_INTERVAL_MS = 180000; // 3 minutes minimum between automatic snapshots

/**
 * Save in-progress editing draft to an isolated slot that cannot be overwritten by rollbacks.
 */
export const saveLiveDraft = (tenantHandle, config) => {
    try {
        if (!config || typeof config !== 'object') return false;
        const tenant = tenantHandle || 'default';
        const key = `${LIVE_DRAFT_PREFIX}${tenant}`;
        const entry = {
            timestamp: Date.now(),
            dateFormatted: new Date().toLocaleString(),
            config
        };
        localStorage.setItem(key, JSON.stringify(entry));
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Retrieve active live draft for tenant
 */
export const getLiveDraft = (tenantHandle) => {
    try {
        const tenant = tenantHandle || 'default';
        const key = `${LIVE_DRAFT_PREFIX}${tenant}`;
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
        return null;
    }
};

/**
 * Clear live draft when changes are saved to server
 */
export const clearLiveDraft = (tenantHandle) => {
    try {
        const tenant = tenantHandle || 'default';
        localStorage.removeItem(`${LIVE_DRAFT_PREFIX}${tenant}`);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Save snapshot of current config and products to browser vault with source classification
 */
export const saveToLocalVault = (
    tenantHandle,
    config,
    products = null,
    orders = null,
    categories = null,
    source = 'system',
    customLabel = null
) => {
    try {
        if (!config || typeof config !== 'object') return false;
        const tenant = tenantHandle || 'default';
        const key = `${VAULT_PREFIX}${tenant}`;
        
        const timestamp = Date.now();

        // Create lightweight summaries to save browser storage space
        const lightProducts = Array.isArray(products) 
            ? products.slice(0, 30).map(p => ({ id: p.id, name: p.name, price: p.price, category: p.category })) 
            : null;

        const lightOrders = Array.isArray(orders)
            ? orders.slice(0, 30).map(o => ({ id: o.id, date: o.date, total: o.total, status: o.status }))
            : null;

        let displayLabel = customLabel;
        if (!displayLabel) {
            if (source === 'draft') displayLabel = '📝 Working Draft';
            else if (source === 'manual') displayLabel = '📸 Manual Local Backup';
            else if (source === 'rollback') displayLabel = '🔄 Pre-Rollback State';
            else displayLabel = '⚡ Auto Local Snapshot';
        }

        const newEntry = {
            id: `local_vault_${timestamp}_${Math.random().toString(36).substring(2, 7)}`,
            timestamp,
            dateFormatted: new Date(timestamp).toLocaleString(),
            source,
            label: displayLabel,
            siteTitle: config?.settings?.siteTitle || 'Omnivaso Site',
            sectionsCount: (config?.sections || []).length,
            pagesCount: (config?.pages || []).length,
            productsCount: Array.isArray(products) ? products.length : 0,
            ordersCount: Array.isArray(orders) ? orders.length : 0,
            categoriesCount: Array.isArray(categories) ? categories.length : 0,
            config,
            products: lightProducts,
            orders: lightOrders,
            categories
        };

        const existingRaw = localStorage.getItem(key);
        let entries = [];
        if (existingRaw) {
            try {
                entries = JSON.parse(existingRaw);
                if (!Array.isArray(entries)) entries = [];
            } catch (e) { entries = []; }
        }

        const configStr = JSON.stringify(config);

        // Deduplication: Avoid storing identical configs
        const duplicateIdx = entries.findIndex(e => JSON.stringify(e.config) === configStr);
        if (duplicateIdx !== -1) {
            // If identical entry exists and was saved recently, skip entirely
            if ((timestamp - entries[duplicateIdx].timestamp) < MIN_SYSTEM_INTERVAL_MS && source === 'system') {
                return true;
            }
            // Remove older duplicate so the fresh one replaces it at the top
            entries.splice(duplicateIdx, 1);
        }

        // Rate limiting for automated system / rollback snapshots:
        // Do not add multiple automated snapshots within 3 minutes of the last one
        if ((source === 'system' || source === 'rollback') && entries.length > 0) {
            const lastAuto = entries.find(e => e.source === 'system' || e.source === 'rollback');
            if (lastAuto && (timestamp - lastAuto.timestamp) < MIN_SYSTEM_INTERVAL_MS && duplicateIdx === -1) {
                // If there's an automated snapshot recorded within the last 3 minutes, don't spam the vault
                return true;
            }
        }

        // Put fresh entry at the front
        entries.unshift(newEntry);

        // Smart pruning: When exceeding MAX_VAULT_ITEMS, prune system/rollback entries first, preserving drafts & manual saves
        if (entries.length > MAX_VAULT_ITEMS) {
            let pruneIdx = -1;
            for (let i = entries.length - 1; i >= 0; i--) {
                if (entries[i].source === 'system' || entries[i].source === 'rollback') {
                    pruneIdx = i;
                    break;
                }
            }
            if (pruneIdx !== -1) {
                entries.splice(pruneIdx, 1);
            } else {
                entries = entries.slice(0, MAX_VAULT_ITEMS);
            }
        }

        // Try saving with progressive fallbacks if LocalStorage quota is exceeded
        for (let count = entries.length; count >= 1; count--) {
            try {
                localStorage.setItem(key, JSON.stringify(entries.slice(0, count)));
                return true;
            } catch (quotaErr) {
                if (count === 1) {
                    try {
                        const ultraLight = [{ ...newEntry, products: null, orders: null }];
                        localStorage.setItem(key, JSON.stringify(ultraLight));
                        return true;
                    } catch (e2) {
                        return false;
                    }
                }
            }
        }
        return false;
    } catch (err) {
        return false;
    }
};

/**
 * Get all stored local vault snapshots for a tenant
 */
export const getLocalVaultSnapshots = (tenantHandle) => {
    try {
        const tenant = tenantHandle || 'default';
        const key = `${VAULT_PREFIX}${tenant}`;
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

/**
 * Clear vault for tenant
 */
export const clearLocalVault = (tenantHandle) => {
    try {
        const tenant = tenantHandle || 'default';
        localStorage.removeItem(`${VAULT_PREFIX}${tenant}`);
        return true;
    } catch (e) {
        return false;
    }
};
